/**
 * PATH: packages/email-hub/src/feature/components/rich-text/tableExtensions.ts
 * FILE_ID: EMAILHUB-RICHTEXT-TABLEEXT-001
 * PURPOSE: Table nodes for the email editor — resizable tables, per-cell
 *          background colour, and a live-recompute formula attribute. The
 *          recompute plugin keeps =SUM(A1:A3) cells in sync as referenced
 *          cells change; emailHtml bakes the displayed value on send.
 * WHY:     Operator asked for Excel-style tables with formulas + cell
 *          colour. Formula maths lives in the pure tableFormula.ts; this
 *          file only wires it into ProseMirror.
 */

import { Extension } from '@tiptap/core'
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import type { Node as PMNode } from '@tiptap/pm/model'
import {
  computeFormula,
  cellTextToNumber,
  formatFormulaValue,
  type FormulaGrid,
} from './tableFormula'

const cellAttrs = {
  backgroundColor: {
    default: null as string | null,
    parseHTML: (el: HTMLElement) => el.style.backgroundColor || el.getAttribute('data-bg') || null,
    renderHTML: (attrs: Record<string, unknown>) =>
      attrs.backgroundColor ? { style: `background-color:${attrs.backgroundColor as string}` } : {},
  },
  formula: {
    default: null as string | null,
    parseHTML: (el: HTMLElement) => el.getAttribute('data-formula'),
    renderHTML: (attrs: Record<string, unknown>) =>
      attrs.formula ? { 'data-formula': attrs.formula as string } : {},
  },
}

const CustomTableCell = TableCell.extend({
  addAttributes() {
    return { ...this.parent?.(), ...cellAttrs }
  },
})

const CustomTableHeader = TableHeader.extend({
  addAttributes() {
    return { ...this.parent?.(), ...cellAttrs }
  },
})

const formulaPluginKey = new PluginKey('tableFormulaRecompute')

interface CellHit {
  innerFrom: number
  innerTo: number
  row: number
  col: number
  formula: string | null
  text: string
}

/** Walk one table node → flat list of cells with grid coords + abs positions. */
function collectCells(table: PMNode, tablePos: number): CellHit[] {
  const hits: CellHit[] = []
  let rowIdx = 0
  table.forEach((row, rowOffset) => {
    if (row.type.name !== 'tableRow') return
    const rowPos = tablePos + 1 + rowOffset
    let colIdx = 0
    row.forEach((cell, cellOffset) => {
      const cellPos = rowPos + 1 + cellOffset
      hits.push({
        innerFrom: cellPos + 1,
        innerTo: cellPos + cell.nodeSize - 1,
        row: rowIdx,
        col: colIdx,
        formula: (cell.attrs.formula as string | null) ?? null,
        text: cell.textContent,
      })
      colIdx += (cell.attrs.colspan as number) || 1
    })
    rowIdx += 1
  })
  return hits
}

function buildGrid(cells: CellHit[]): FormulaGrid {
  const rows = cells.reduce((m, c) => Math.max(m, c.row + 1), 0)
  const cols = cells.reduce((m, c) => Math.max(m, c.col + 1), 0)
  const grid: (number | null)[][] = Array.from({ length: rows }, () => Array<number | null>(cols).fill(null))
  for (const c of cells) {
    // A formula cell contributes its displayed value (computed text).
    grid[c.row][c.col] = cellTextToNumber(c.text)
  }
  return grid
}

/**
 * appendTransaction recompute: after any content change, refresh every cell
 * that carries a `formula` attribute. Replacements are applied in DESCENDING
 * position order so earlier offsets stay valid; convergence is guaranteed
 * because a settled cell already equals its computed text (no further edit).
 */
const TableFormula = Extension.create({
  name: 'tableFormula',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: formulaPluginKey,
        appendTransaction(transactions, _oldState, newState) {
          if (!transactions.some((t) => t.docChanged)) return null
          const edits: Array<{ from: number; to: number; text: string }> = []
          newState.doc.descendants((node, pos) => {
            if (node.type.name !== 'table') return undefined
            const cells = collectCells(node, pos)
            if (!cells.some((c) => c.formula)) return false
            const grid = buildGrid(cells)
            for (const c of cells) {
              if (!c.formula) continue
              const res = computeFormula(c.formula, grid)
              const next = res.ok ? formatFormulaValue(res.value) : '#ERR'
              if (c.text !== next) edits.push({ from: c.innerFrom, to: c.innerTo, text: next })
            }
            return false
          })
          if (edits.length === 0) return null
          const tr = newState.tr
          const paragraph = newState.schema.nodes.paragraph
          edits
            .sort((a, b) => b.from - a.from)
            .forEach(({ from, to, text }) => {
              const para = paragraph.create(null, text ? newState.schema.text(text) : [])
              tr.replaceWith(from, to, para)
            })
          tr.setMeta('addToHistory', false)
          return tr.steps.length ? tr : null
        },
      }),
    ]
  },
})

/** Assemble the table extension set for the email editor. */
export function buildTableExtensions() {
  return [
    Table.configure({ resizable: true, allowTableNodeSelection: true }),
    TableRow,
    CustomTableHeader,
    CustomTableCell,
    TableFormula,
  ]
}
